import React, { useEffect, useState } from 'react';
import {
  Box, Typography, TextField, Grid, Chip, Card, IconButton,
  MenuItem, InputAdornment, CircularProgress, Alert, Snackbar,
  Checkbox, FormControlLabel, Popover, Modal, Button, Avatar, Divider, Tooltip
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Search, CalendarToday, Clear, Refresh, Email, Phone,
  Person, ViewColumn, Home, Business, Apartment, Villa,
  MessageOutlined, Download, Close, TrendingUp, FilterList
} from '@mui/icons-material';
import { investorApi } from '../../api/api';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

/* ── Config ──────────────────────────────────────────────── */
const TYPE_CONFIG = {
  Property:         { color: '#F59E0B', bg: '#FFFBEB', icon: <Home />,      label: 'Property'         },
  Project:          { color: '#10B981', bg: '#ECFDF5', icon: <Business />,  label: 'Project'          },
  Apartment:        { color: '#6366F1', bg: '#EEF2FF', icon: <Apartment />, label: 'Apartment'        },
  'Individual Villa':{ color: '#EC4899', bg: '#FDF2F8', icon: <Villa />,    label: 'Individual Villa' },
};

const getTypeInfo = (type) =>
  TYPE_CONFIG[type] || { color: '#6B7280', bg: '#F3F4F6', icon: <Home />, label: type || 'Unknown' };

/* ── Stat Card ───────────────────────────────────────────── */
const StatCard = ({ label, value, color, icon, total }) => (
  <Card elevation={0} sx={{
    p: 2.5, borderRadius: 3, border: '1px solid #E5E7EB', bgcolor: '#fff',
    transition: 'box-shadow .2s', cursor: 'default',
    '&:hover': { boxShadow: '0 8px 24px rgba(0,0,0,.08)' }
  }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <Box>
        <Typography sx={{ fontSize: 12, color: '#9CA3AF', fontWeight: 600, letterSpacing: '.04em', mb: 0.5 }}>
          {label.toUpperCase()}
        </Typography>
        <Typography sx={{ fontSize: 30, fontWeight: 800, color, lineHeight: 1 }}>{value}</Typography>
        {total != null && (
          <Typography sx={{ fontSize: 11, color: '#9CA3AF', mt: 0.5 }}>
            {total > 0 ? `${((value / total) * 100).toFixed(0)}% of total` : '—'}
          </Typography>
        )}
      </Box>
      <Avatar sx={{ bgcolor: color + '18', width: 44, height: 44 }}>
        {React.cloneElement(icon, { sx: { color, fontSize: 20 } })}
      </Avatar>
    </Box>
  </Card>
);

/* ── Type Badge ──────────────────────────────────────────── */
const TypeBadge = ({ type }) => {
  const cfg = getTypeInfo(type);
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.6,
      px: 1.5, py: 0.4, borderRadius: 20,
      bgcolor: cfg.bg, color: cfg.color,
      fontSize: 11, fontWeight: 700, border: `1px solid ${cfg.color}25`,
      whiteSpace: 'nowrap'
    }}>
      {React.cloneElement(cfg.icon, { sx: { fontSize: 12 } })}
      {cfg.label}
    </Box>
  );
};

/* ── Detail Row ──────────────────────────────────────────── */
const DetailRow = ({ label, value, highlight }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
    <Typography sx={{ fontSize: 11, color: '#9CA3AF', fontWeight: 600, letterSpacing: '.04em' }}>
      {label.toUpperCase()}
    </Typography>
    <Typography sx={{ fontSize: 14, color: highlight ? '#6366F1' : '#111827', fontWeight: 500 }}>
      {value || '—'}
    </Typography>
  </Box>
);

/* ── Main Component ──────────────────────────────────────── */
const Enquiry = () => {
  const [enquiries,          setEnquiries]          = useState([]);
  const [filteredEnquiries,  setFilteredEnquiries]  = useState([]);
  const [searchKeyword,      setSearchKeyword]      = useState('');
  const [loading,            setLoading]            = useState(true);
  const [snackbar,           setSnackbar]           = useState({ open: false, message: '', severity: 'success' });
  const [pageSize,           setPageSize]           = useState(25);
  const [propertyTypeFilter, setPropertyTypeFilter] = useState('All');
  const [dateFilter,         setDateFilter]         = useState('');
  const [anchorEl,           setAnchorEl]           = useState(null);
  const [openViewModal,      setOpenViewModal]      = useState(false);
  const [selectedEnquiry,    setSelectedEnquiry]    = useState(null);

  const allColumns = [
    { field: 'EnquiryID',     headerName: '#',          width: 70  },
    { field: 'Username',      headerName: 'USER',       width: 180 },
    { field: 'Mobilenumber',  headerName: 'MOBILE',     width: 150 },
    { field: 'Email',         headerName: 'EMAIL',      width: 220 },
    { field: 'ApartmentName', headerName: 'APARTMENT',  width: 180 },
    { field: 'PropertyName',  headerName: 'PROPERTY',   width: 180 },
    { field: 'project_name',  headerName: 'PROJECT',    width: 180 },
    { field: 'PropertyType',  headerName: 'TYPE',       width: 150 },
    { field: 'Message',       headerName: 'MESSAGE',    width: 250 },
    { field: 'created_on',    headerName: 'DATE & TIME',width: 160 },
  ];
  const [selectedFields, setSelectedFields] = useState(allColumns.map(c => c.field));

  const showSnackbar = (msg, sev = 'success') => setSnackbar({ open: true, message: msg, severity: sev });
  const formatDate   = (d) => d ? dayjs(d).format('DD MMM YYYY, hh:mm A') : '—';

  const applyFilters = (data, typeF, dateF, searchF) => {
    let f = data;
    if (typeF !== 'All')   f = f.filter(e => e.PropertyType === typeF);
    if (dateF)             f = f.filter(e => e.created_on?.startsWith(dateF));
    if (searchF.trim()) {
      const kw = searchF.toLowerCase();
      f = f.filter(e =>
        [e.Username, e.PropertyName, e.ApartmentName, e.project_name,
         e.Mobilenumber?.toString(), e.Email, e.Message]
          .some(s => s?.toLowerCase().includes(kw))
      );
    }
    setFilteredEnquiries(f);
  };

  const fetchEnquiries = async () => {
    setLoading(true);
    try {
      const res  = await investorApi.getAllEnquiries();
      const data = res.data || [];
      setEnquiries(data);
      applyFilters(data, propertyTypeFilter, dateFilter, searchKeyword);
    } catch (err) {
      showSnackbar('Failed to load enquiries', 'error');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchEnquiries(); }, []);
  useEffect(() => { applyFilters(enquiries, propertyTypeFilter, dateFilter, searchKeyword); },
    [propertyTypeFilter, dateFilter, searchKeyword, enquiries]);

  const clearFilters = () => { setPropertyTypeFilter('All'); setDateFilter(''); setSearchKeyword(''); };
  const hasFilters   = propertyTypeFilter !== 'All' || dateFilter || searchKeyword;

  const handleExport = () => {
    const rows = filteredEnquiries.map(e => ({
      ID: e.EnquiryID, User: e.Username, Mobile: e.Mobilenumber, Email: e.Email,
      Apartment: e.ApartmentName, Property: e.PropertyName, Project: e.project_name,
      Type: e.PropertyType, Message: e.Message,
      Date: formatDate(e.created_on),
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Enquiries');
    saveAs(new Blob([XLSX.write(wb, { bookType: 'xlsx', type: 'array' })],
      { type: 'application/octet-stream' }), `Enquiries_${dayjs().format('DD-MM-YYYY')}.xlsx`);
  };

  /* ── Columns ── */
  const columnDefs = [
    {
      field: 'EnquiryID', headerName: '#', width: 70,
      headerAlign: 'center', align: 'center',
      renderCell: (p) => (
        <Typography sx={{ fontSize: 12, color: '#9CA3AF', fontWeight: 600 }}>#{p.value}</Typography>
      ),
    },
    {
      field: 'Username', headerName: 'USER', width: 180,
      renderCell: (p) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ width: 32, height: 32, bgcolor: '#6366F1', fontSize: 12, fontWeight: 700 }}>
            {p.value?.charAt(0).toUpperCase() || 'U'}
          </Avatar>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#111827' }} noWrap>{p.value || '—'}</Typography>
        </Box>
      ),
    },
    {
      field: 'Mobilenumber', headerName: 'MOBILE', width: 150,
      renderCell: (p) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <Phone sx={{ fontSize: 13, color: '#9CA3AF' }} />
          <Typography sx={{ fontSize: 13, color: '#6366F1', fontWeight: 500 }}>{p.value || '—'}</Typography>
        </Box>
      ),
    },
    {
      field: 'Email', headerName: 'EMAIL', width: 220,
      renderCell: (p) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <Email sx={{ fontSize: 13, color: '#9CA3AF' }} />
          <Typography sx={{ fontSize: 13, color: '#6366F1', fontWeight: 500 }} noWrap>{p.value || '—'}</Typography>
        </Box>
      ),
    },
    {
      field: 'ApartmentName', headerName: 'APARTMENT', width: 180,
      renderCell: (p) => <Typography sx={{ fontSize: 13, color: '#374151' }} noWrap>{p.value || '—'}</Typography>,
    },
    {
      field: 'PropertyName', headerName: 'PROPERTY', width: 180,
      renderCell: (p) => <Typography sx={{ fontSize: 13, color: '#374151' }} noWrap>{p.value || '—'}</Typography>,
    },
    {
      field: 'project_name', headerName: 'PROJECT', width: 180,
      renderCell: (p) => <Typography sx={{ fontSize: 13, color: '#374151' }} noWrap>{p.value || '—'}</Typography>,
    },
    {
      field: 'PropertyType', headerName: 'TYPE', width: 150,
      renderCell: (p) => <TypeBadge type={p.value} />,
    },
    {
      field: 'Message', headerName: 'MESSAGE', width: 250,
      renderCell: (p) => (
        <Typography sx={{ fontSize: 13, color: p.value ? '#374151' : '#9CA3AF',
          fontStyle: p.value ? 'normal' : 'italic', overflow: 'hidden',
          textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>
          {p.value || 'No message'}
        </Typography>
      ),
    },
    {
      field: 'created_on', headerName: 'DATE', width: 160,
      renderCell: (p) => (
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
            {p.value ? dayjs(p.value).format('DD MMM YYYY') : '—'}
          </Typography>
          <Typography sx={{ fontSize: 11, color: '#9CA3AF' }}>
            {p.value ? dayjs(p.value).format('hh:mm A') : ''}
          </Typography>
        </Box>
      ),
    },
  ];

  const displayedColumns = columnDefs.filter(c => selectedFields.includes(c.field));
  const total = enquiries.length;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: '#F9FAFB', minHeight: '100vh' }}>

      {/* ── Header ── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography sx={{ fontSize: 24, fontWeight: 700, color: '#111827' }}>Property Enquiries</Typography>
          <Typography sx={{ fontSize: 14, color: '#6B7280', mt: 0.5 }}>
            View and manage all incoming property enquiries
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchEnquiries} sx={{ border: '1px solid #E5E7EB', borderRadius: 2,
              '&:hover': { borderColor: '#6366F1', color: '#6366F1', bgcolor: '#EEF2FF' } }}>
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button onClick={handleExport} startIcon={<Download />} variant="contained" size="small"
            sx={{ borderRadius: 2, textTransform: 'none', bgcolor: '#6366F1',
              boxShadow: '0 2px 8px rgba(99,102,241,.3)',
              '&:hover': { bgcolor: '#4F46E5' } }}>
            Export Excel
          </Button>
        </Box>
      </Box>

      {/* ── Stat Cards ── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={2.4}>
          <StatCard label="Total"     value={total}                                              color="#6366F1" icon={<TrendingUp />} />
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <StatCard label="Property"  value={enquiries.filter(e => e.PropertyType === 'Property').length}          color="#F59E0B" icon={<Home />}      total={total} />
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <StatCard label="Project"   value={enquiries.filter(e => e.PropertyType === 'Project').length}           color="#10B981" icon={<Business />}  total={total} />
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <StatCard label="Apartment" value={enquiries.filter(e => e.PropertyType === 'Apartment').length}         color="#6366F1" icon={<Apartment />} total={total} />
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <StatCard label="Villa"     value={enquiries.filter(e => e.PropertyType === 'Individual Villa').length}  color="#EC4899" icon={<Villa />}     total={total} />
        </Grid>
      </Grid>

      {/* ── Filters ── */}
      <Card elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid #E5E7EB', bgcolor: '#fff' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField placeholder="Search by name, email, mobile, message..."
              fullWidth size="small" value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Search sx={{ color: '#9CA3AF', fontSize: 18 }} /></InputAdornment>,
                endAdornment: searchKeyword && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchKeyword('')}>
                      <Clear sx={{ fontSize: 16 }} />
                    </IconButton>
                  </InputAdornment>
                ),
                sx: { borderRadius: 2, fontSize: 13 }
              }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField type="date" fullWidth size="small" label="Date"
              value={dateFilter} onChange={(e) => setDateFilter(e.target.value)}
              InputLabelProps={{ shrink: true }}
              InputProps={{ sx: { borderRadius: 2, fontSize: 13 } }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField select fullWidth size="small" label="Property Type"
              value={propertyTypeFilter} onChange={(e) => setPropertyTypeFilter(e.target.value)}
              InputProps={{ sx: { borderRadius: 2, fontSize: 13 } }}>
              <MenuItem value="All">All Types</MenuItem>
              {Object.keys(TYPE_CONFIG).map(t => (
                <MenuItem key={t} value={t}><TypeBadge type={t} /></MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} md={2}>
            {hasFilters ? (
              <Button onClick={clearFilters} fullWidth variant="outlined" size="small" startIcon={<Clear />}
                sx={{ borderRadius: 2, textTransform: 'none', fontSize: 13,
                  borderColor: '#EF4444', color: '#EF4444',
                  '&:hover': { bgcolor: '#FEF2F2', borderColor: '#EF4444' } }}>
                Clear Filters
              </Button>
            ) : (
              <Typography sx={{ fontSize: 12, color: '#9CA3AF', textAlign: 'center' }}>No filters active</Typography>
            )}
          </Grid>
          <Grid item xs={6} md={2} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Tooltip title="Show/Hide Columns">
              <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}
                sx={{ border: '1px solid #E5E7EB', borderRadius: 2,
                  '&:hover': { borderColor: '#6366F1', color: '#6366F1', bgcolor: '#EEF2FF' } }}>
                <ViewColumn fontSize="small" />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
          <Typography sx={{ fontSize: 12, color: '#6B7280' }}>
            Showing <strong>{filteredEnquiries.length}</strong> of <strong>{total}</strong> enquiries
          </Typography>
          {hasFilters && (
            <Chip label="Filtered" size="small" color="primary" variant="outlined"
              sx={{ height: 18, fontSize: 10, '& .MuiChip-label': { px: 1 } }} />
          )}
        </Box>
      </Card>

      {/* ── Data Grid ── */}
      <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #E5E7EB', overflow: 'hidden' }}>
        <Box sx={{ height: 600 }}>
          {loading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', gap: 2 }}>
              <CircularProgress sx={{ color: '#6366F1' }} />
              <Typography sx={{ color: '#6B7280', fontSize: 14 }}>Loading enquiries...</Typography>
            </Box>
          ) : (
            <DataGrid
              rows={filteredEnquiries}
              columns={displayedColumns}
              getRowId={(row) => row.EnquiryID}
              pageSize={pageSize}
              rowsPerPageOptions={[25, 50, 100]}
              onPageSizeChange={(n) => setPageSize(n)}
              disableSelectionOnClick
              rowHeight={62}
              onRowClick={(p) => { setSelectedEnquiry(p.row); setOpenViewModal(true); }}
              sx={{
                border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                '& .MuiDataGrid-columnHeaders': {
                  bgcolor: '#F8FAFC', borderBottom: '2px solid #E5E7EB',
                  fontSize: 11, fontWeight: 700, color: '#6B7280', letterSpacing: '.06em',
                },
                '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus': { outline: 'none' },
                '& .MuiDataGrid-row': { borderBottom: '1px solid #F3F4F6', '&:hover': { bgcolor: '#F5F3FF' } },
                '& .MuiDataGrid-cell': { border: 'none', py: 1 },
                '& .MuiDataGrid-footerContainer': { borderTop: '1px solid #E5E7EB', bgcolor: '#F8FAFC' },
              }}
            />
          )}
        </Box>
      </Card>

      {/* ── Column Picker Popover ── */}
      <Popover open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ elevation: 3, sx: { borderRadius: 2, mt: 1, minWidth: 200 } }}>
        <Box sx={{ p: 2 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#374151', mb: 1.5 }}>Show / Hide Columns</Typography>
          <Divider sx={{ mb: 1.5 }} />
          {allColumns.map((col) => (
            <FormControlLabel key={col.field} sx={{ display: 'flex', m: 0, py: 0.3 }}
              control={
                <Checkbox size="small" checked={selectedFields.includes(col.field)}
                  onChange={() => setSelectedFields(prev =>
                    prev.includes(col.field) ? prev.filter(f => f !== col.field) : [...prev, col.field]
                  )}
                  sx={{ '&.Mui-checked': { color: '#6366F1' }, p: 0.5 }}
                />
              }
              label={<Typography sx={{ fontSize: 13, color: '#374151' }}>{col.headerName}</Typography>}
            />
          ))}
        </Box>
      </Popover>

      {/* ── Detail Modal ── */}
      <Modal open={openViewModal} onClose={() => setOpenViewModal(false)}>
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
          width: { xs: '92%', sm: 560 }, bgcolor: '#fff', borderRadius: 3,
          boxShadow: '0 25px 50px rgba(0,0,0,.15)', overflow: 'hidden', maxHeight: '90vh',
        }}>
          {/* Modal Header */}
          <Box sx={{ px: 3, py: 2.5, bgcolor: '#6366F1', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>Enquiry Details</Typography>
              {selectedEnquiry && (
                <Typography sx={{ fontSize: 12, color: '#C7D2FE', mt: 0.3 }}>
                  #{selectedEnquiry.EnquiryID} · {formatDate(selectedEnquiry.created_on)}
                </Typography>
              )}
            </Box>
            <IconButton onClick={() => setOpenViewModal(false)} sx={{ color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,.15)' } }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>

          {selectedEnquiry && (
            <Box sx={{ p: 3, overflowY: 'auto', maxHeight: 'calc(90vh - 130px)' }}>

              {/* User Card */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: '#F8FAFC', borderRadius: 2, mb: 3 }}>
                <Avatar sx={{ width: 48, height: 48, bgcolor: '#6366F1', fontSize: 18, fontWeight: 700 }}>
                  {selectedEnquiry.Username?.charAt(0).toUpperCase() || 'U'}
                </Avatar>
                <Box>
                  <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>{selectedEnquiry.Username}</Typography>
                  <Box sx={{ display: 'flex', gap: 2, mt: 0.3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Phone sx={{ fontSize: 13, color: '#9CA3AF' }} />
                      <Typography sx={{ fontSize: 13, color: '#6366F1' }}>{selectedEnquiry.Mobilenumber}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Email sx={{ fontSize: 13, color: '#9CA3AF' }} />
                      <Typography sx={{ fontSize: 13, color: '#6366F1' }}>{selectedEnquiry.Email}</Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Property Info */}
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', letterSpacing: '.06em', mb: 1.5 }}>
                PROPERTY INFORMATION
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <DetailRow label="Type" value={<TypeBadge type={selectedEnquiry.PropertyType} />} />
                </Grid>
                <Grid item xs={6}>
                  <DetailRow label="Property" value={selectedEnquiry.PropertyName} />
                </Grid>
                {selectedEnquiry.ApartmentName && (
                  <Grid item xs={6}>
                    <DetailRow label="Apartment" value={selectedEnquiry.ApartmentName} />
                  </Grid>
                )}
                {selectedEnquiry.project_name && (
                  <Grid item xs={6}>
                    <DetailRow label="Project" value={selectedEnquiry.project_name} />
                  </Grid>
                )}
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Message */}
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', letterSpacing: '.06em', mb: 1.5 }}>
                MESSAGE
              </Typography>
              <Box sx={{ p: 2, bgcolor: '#F8FAFC', borderRadius: 2, border: '1px solid #E5E7EB', minHeight: 80 }}>
                <Typography sx={{ fontSize: 14, color: selectedEnquiry.Message ? '#374151' : '#9CA3AF',
                  fontStyle: selectedEnquiry.Message ? 'normal' : 'italic', lineHeight: 1.7 }}>
                  {selectedEnquiry.Message || 'No message provided'}
                </Typography>
              </Box>
            </Box>
          )}

          <Box sx={{ px: 3, py: 2, bgcolor: '#F8FAFC', borderTop: '1px solid #E5E7EB', display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={() => setOpenViewModal(false)} variant="contained" size="small"
              sx={{ borderRadius: 2, textTransform: 'none', bgcolor: '#6366F1',
                '&:hover': { bgcolor: '#4F46E5' } }}>
              Close
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* ── Snackbar ── */}
      <Snackbar open={snackbar.open} autoHideDuration={4000}
        onClose={() => setSnackbar(p => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(p => ({ ...p, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Enquiry;
